import { createAppRequest } from '@/services/request';

const request = createAppRequest();
const base = '/bff/cms/content';

export type ContentAssetChannel = 'xiaohongshu' | 'zhihu' | 'wechat' | 'site';

export const contentChannelLabels: Record<ContentAssetChannel, string> = {
  xiaohongshu: '小红书',
  zhihu: '知乎',
  wechat: '微信公众号',
  site: '本站',
};

export type ContentAssetType =
  | 'master'
  | 'product'
  | 'solution'
  | 'case'
  | 'article'
  | 'faq'
  | 'channel_version'
  | 'material'
  | 'video'
  | 'audio';

export type ContentAssetStatus =
  | 'draft'
  | 'pending_review'
  | 'approved'
  | 'rejected'
  | 'published'
  | 'needs_update'
  | 'withdrawn'
  | 'archived'
  | 'expired';

export type ContentBlockType =
  | 'heading'
  | 'paragraph'
  | 'quote'
  | 'list'
  | 'faq'
  | 'cta';

export interface ContentBlockRecord {
  id: string;
  type: ContentBlockType;
  title?: string;
  content: string;
}

export interface ContentAssetRecord {
  uuid: string;
  title: string;
  summary: string | null;
  contentType: ContentAssetType;
  channel?: ContentAssetChannel | null;
  brandUuid: string | null;
  brandName: string | null;
  productUuid: string | null;
  categoryUuid: string | null;
  categoryName?: string | null;
  productName: string | null;
  ownerMemberUuid: string;
  ownerName: string;
  status: ContentAssetStatus;
  tags: string[];
  bodyMarkdown: string;
  mediaMaterialUuid?: string | null;
  /** @deprecated 兼容旧客户端；正文请使用 bodyMarkdown。 */
  blocks: ContentBlockRecord[];
  references: string[];
  intent: string | null;
  applicableScenarios: string[];
  citationScope: string | null;
  seoTitle?: string | null;
  seoDescription?: string | null;
  slug?: string | null;
  canonicalUrl?: string | null;
  coverUrl?: string | null;
  coverMaterialUuid?: string | null;
  author?: string | null;
  expertUuid?: string | null;
  subtitle?: string | null;
  copyrightType?: 'original' | 'repost' | null;
  source?: string | null;
  revision: number;
  version: string;
  publishedVersionUuid: string | null;
  publishedVersionNo: number;
  publishedAt: string | null;
  healthScore: null;
  seoScore: null;
  channelCount: null;
  viewCount: null;
  leadCount: null;
  createdAt: string;
  updatedAt: string;
}

export interface ContentAssetDetail extends ContentAssetRecord {
  affectedSites: Array<{ uuid: string; name: string }>;
  publishedVersion: {
    uuid: string;
    versionNo: number;
    publishedAt: string;
    publishedByUserId: string;
  } | null;
  operationLogs: Array<{
    action: string;
    actorUserId: string;
    reason: string | null;
    fromStatus: ContentAssetStatus | null;
    toStatus: ContentAssetStatus | null;
    createdAt: string;
  }>;
}

export interface PublishSiteInput {
  siteUuid: string;
  categoryUuid?: string;
  coverMaterialUuid?: string;
}

export interface PublishSiteResult extends PublishSiteInput {
  status: 'published' | 'failed';
  errorCode?: string;
  message?: string;
}

export interface ContentAssetListResult {
  data: ContentAssetRecord[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  typeCounts: Record<ContentAssetType, number>;
  capabilities: {
    health: 'not_connected';
    seo: 'not_connected';
    channel: 'not_connected';
    analytics: 'not_connected';
  };
}

export interface ContentAssetQuery {
  page?: number;
  limit?: number;
  keyword?: string;
  contentType?: ContentAssetType;
  channel?: ContentAssetChannel;
  brandUuid?: string;
  productUuid?: string;
  status?: ContentAssetStatus;
  ownerMemberUuid?: string;
  updatedFrom?: string;
  updatedTo?: string;
  siteUuid?: string;
  excludeSiteUuid?: string;
  categoryUuid?: string;
  tag?: string;
  tags?: string[];
}

export interface ArticleCategory {
  uuid: string;
  name: string;
  parentUuid?: string | null;
  sortOrder?: number;
  description?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface ArticleTag {
  uuid: string;
  name: string;
  color?: string | null;
  sortOrder?: number;
  description?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface ContentAssetPayload {
  title: string;
  summary?: string;
  contentType: ContentAssetType;
  channel?: ContentAssetChannel;
  brandUuid?: string;
  productUuid?: string;
  ownerMemberUuid?: string;
  tags?: string[];
  bodyMarkdown?: string;
  mediaMaterialUuid?: string | null;
  /** @deprecated 兼容旧客户端；正文请使用 bodyMarkdown。 */
  blocks?: ContentBlockRecord[];
  references?: string[];
  intent?: string;
  applicableScenarios?: string[];
  citationScope?: string;
  seoTitle?: string;
  seoDescription?: string;
  slug?: string;
  canonicalUrl?: string;
  coverUrl?: string;
  coverMaterialUuid?: string;
  author?: string;
  expertUuid?: string | null;
  subtitle?: string;
  copyrightType?: 'original' | 'repost';
  source?: string;
  siteUuid?: string;
  categoryUuid?: string;
}

export interface ContentOption {
  uuid: string;
  name: string;
  entityType: string;
}

export interface ContentMemberOption {
  id: string;
  memberUuid: string;
  name?: string;
  nickname?: string;
  username?: string;
  label: string;
}

export interface ContentExpertOption {
  uuid: string;
  name: string;
  entityIdentifier?: string | null;
  jobTitle?: string | null;
  affiliation?: string | null;
}

export interface ContentAssetOptions {
  brands: ContentOption[];
  products: ContentOption[];
  experts?: ContentExpertOption[];
  members: ContentMemberOption[];
  categories: ArticleCategory[];
  tags: ArticleTag[];
}

export type ContentAssetUpdate = Partial<
  Omit<ContentAssetPayload, 'siteUuid' | 'brandUuid' | 'productUuid' | 'categoryUuid'>
> & {
  expectedRevision: number;
  brandUuid?: string | null;
  productUuid?: string | null;
  categoryUuid?: string | null;
  tags?: string[] | null;
  coverUrl?: string | null;
  coverMaterialUuid?: string | null;
  author?: string | null;
  expertUuid?: string | null;
  summary?: string | null;
  subtitle?: string | null;
  copyrightType?: 'original' | 'repost' | null;
  source?: string | null;
};

export interface BatchActionResultItem {
  uuid: string;
  title?: string;
  success: boolean;
  reason?: string;
  error?: string;
}

export interface BatchActionResponse {
  total: number;
  successCount: number;
  failedCount: number;
  succeeded: number;
  failed: number;
  results: BatchActionResultItem[];
}

export const contentManagementApi = {

  list: (params: ContentAssetQuery) =>
    request<ContentAssetListResult>(base, {
      method: 'GET', params,
      paramsSerializer: (query) => {
        const search = new URLSearchParams();
        Object.entries(query).forEach(([key, value]) => {
          if (value == null) return;
          (Array.isArray(value) ? value : [value]).forEach((item) => search.append(key, String(item)));
        });
        return search.toString();
      },
    }),
  options: () => request<ContentAssetOptions>(`${base}/options`),
  get: (uuid: string) => request<ContentAssetDetail>(`${base}/${uuid}`),
  create: (data: ContentAssetPayload) =>
    request<ContentAssetDetail>(base, { method: 'POST', data }),
  createForChannels: (data: {
    title: string;
    bodyMarkdown: string;
    channels: ContentAssetChannel[];
    siteUuid?: string;
  }) => request<{ data: ContentAssetRecord[] }>(`${base}/create-for-channels`, {
    method: 'POST', data,
  }),
  update: (uuid: string, data: ContentAssetUpdate) =>
    request<ContentAssetDetail>(`${base}/${uuid}`, { method: 'PATCH', data }),
  submitReview: (uuid: string) =>
    request<ContentAssetDetail>(`${base}/${uuid}/submit-review`, {
      method: 'POST',
      data: {},
    }),
  review: (uuid: string, decision: 'approve' | 'reject', reason?: string) =>
    request<ContentAssetDetail>(`${base}/${uuid}/review`, {
      method: 'POST',
      data: { decision, reason },
    }),
  publish: (uuid: string, data?: { sites?: PublishSiteInput[] }) =>
    request<ContentAssetDetail & { results?: PublishSiteResult[] }>(`${base}/${uuid}/publish`, { method: 'POST', data: data || {} }),
  revise: (uuid: string, reason?: string) =>
    request<ContentAssetDetail>(`${base}/${uuid}/revise`, {
      method: 'POST',
      data: { reason },
    }),
  withdraw: (uuid: string, reason?: string) =>
    request<ContentAssetDetail>(`${base}/${uuid}/withdraw`, {
      method: 'POST',
      data: { reason },
    }),
  archive: (uuid: string, reason?: string) =>
    request<ContentAssetDetail>(`${base}/${uuid}/archive`, {
      method: 'POST',
      data: { reason },
    }),
  restore: (uuid: string, reason?: string) =>
    request<ContentAssetDetail>(`${base}/${uuid}/restore`, {
      method: 'POST',
      data: { reason },
    }),
  batchAction: (uuids: string[], action: 'submit_review' | 'archive') =>
    request<BatchActionResponse>(`${base}/batch-action`, {
      method: 'POST',
      data: { uuids, action },
    }),
  delete: (uuid: string) =>
    request<{ success: boolean; uuid: string }>(`${base}/${uuid}`, {
      method: 'DELETE',
    }),
  polish: (data: { title: string; bodyMarkdown: string; instruction?: string }) =>
    request<{ title: string; bodyMarkdown: string }>(`${base}/polish`, {
      method: 'POST',
      data,
    }),
  generateSeo: (data: GenerateSeoInput) =>
    request<GenerateSeoResult>(`${base}/generate-seo`, {
      method: 'POST',
      data,
    }),
  listCategories: () =>
    request<ArticleCategory[]>(`${base}/categories`, {
      method: 'GET',
    }),
  createCategory: (data: {
    name: string;
    parentUuid?: string;
    sortOrder?: number;
    description?: string;
  }) =>
    request<ArticleCategory>(`${base}/categories`, {
      method: 'POST',
      data,
    }),
  updateCategory: (
    uuid: string,
    data: {
      name?: string;
      parentUuid?: string | null;
      sortOrder?: number;
      description?: string | null;
    },
  ) =>
    request<ArticleCategory>(`${base}/categories/${uuid}`, {
      method: 'PATCH',
      data,
    }),
  deleteCategory: (uuid: string) =>
    request<{ success: boolean }>(`${base}/categories/${uuid}`, {
      method: 'DELETE',
    }),
  listTags: () =>
    request<ArticleTag[]>(`${base}/tags`, {
      method: 'GET',
    }),
  createTag: (data: {
    name: string;
    color?: string;
    sortOrder?: number;
    description?: string;
  }) =>
    request<ArticleTag>(`${base}/tags`, {
      method: 'POST',
      data,
    }),
  updateTag: (
    uuid: string,
    data: {
      name?: string;
      color?: string | null;
      sortOrder?: number;
      description?: string | null;
    },
  ) =>
    request<ArticleTag>(`${base}/tags/${uuid}`, {
      method: 'PATCH',
      data,
    }),
  deleteTag: (uuid: string) =>
    request<{ success: boolean }>(`${base}/tags/${uuid}`, {
      method: 'DELETE',
    }),
};

export interface GenerateSeoInput {
  title: string;
  bodyMarkdown?: string;
  summary?: string;
  contentUuid?: string;
  siteUuid?: string;
}

export interface GenerateSeoResult {
  seoTitle: string;
  seoDescription: string;
  slug: string;
  canonicalUrl: string;
}


export interface AiTopicItem {
  uuid: string;
  title: string;
  summary: string | null;
  status: ContentAssetStatus;
  bodyMarkdown: string;
  tags: string[];
  createdAt: string;
  updatedAt: string;
  ownerName?: string;
}

export interface AiTopicListQuery {
  page?: number;
  limit?: number;
  keyword?: string;
  status?: string;
}

export interface AiTopicListResult {
  data: AiTopicItem[];
  total: number;
  page: number;
  limit: number;
}

export type AiTopicDirection =
  | '产品与解决方案'
  | '行业知识与科普'
  | '用户痛点与决策'
  | '品牌与案例';

export interface AiTopicPlanningInput {
  sourceType: 'content-center' | 'website';
  websiteUrl?: string;
  scopeText?: string;
  direction: AiTopicDirection;
}

export interface AiTopicCandidate {
  title: string;
  angle: string;
}

export const aiTopicApi = {
  list: (params?: AiTopicListQuery) =>
    request<AiTopicListResult>('/bff/cms/ai/topic', {
      method: 'GET',
      params,
    }),
  get: (uuid: string) =>
    request<AiTopicItem>(`/bff/cms/ai/topic/${uuid}`, {
      method: 'GET',
    }),
  create: (data: { title: string; bodyMarkdown?: string; summary?: string }) =>
    request<AiTopicItem>('/bff/cms/ai/topic', {
      method: 'POST',
      data,
    }),
  update: (
    uuid: string,
    data: {
      title?: string;
      bodyMarkdown?: string;
      summary?: string;
      status?: ContentAssetStatus;
    },
  ) =>
    request<AiTopicItem>(`/bff/cms/ai/topic/${uuid}`, {
      method: 'PATCH',
      data,
    }),
  delete: (uuid: string) =>
    request<{ success: boolean; uuid: string }>(`/bff/cms/ai/topic/${uuid}`, {
      method: 'DELETE',
    }),
  generate: (title: string) =>
    request<ContentAssetDetail>('/bff/cms/ai/topic/generate', {
      method: 'POST',
      data: { title },
    }),
  generateCandidates: (data: AiTopicPlanningInput & { count: number }) =>
    request<{ candidates: AiTopicCandidate[] }>('/bff/cms/ai/topic/generate-candidates', {
      method: 'POST',
      data,
    }),
  suggestScopes: (data: AiTopicPlanningInput) =>
    request<{ scopes: string[] }>('/bff/cms/ai/topic/suggest-scopes', {
      method: 'POST',
      data,
    }),
  publish: (uuid: string, data?: { siteUuid?: string }) =>
    request<AiTopicItem>(`/bff/cms/ai/topic/${uuid}/publish`, {
      method: 'POST',
      data,
    }),
  generateArticle: (
    uuid: string,
    data?: {
      siteUuid?: string;
      topicAction?: 'archive' | 'delete';
      prompt?: string;
    },
  ) =>
    request<{
      article: ContentAssetDetail;
      topicUuid: string;
      topicAction: 'archive' | 'delete';
      topicStatus: ContentAssetStatus;
    }>(`/bff/cms/ai/topic/${uuid}/generate-article`, {
      method: 'POST',
      data,
    }),
};

export interface WorkBuddyTopicItem {
  uuid: string;
  tenantUuid?: string;
  title: string;
  summary: string;
  status: ContentAssetStatus | string;
  source: string;
  tags: string[];
  seo?: {
    seoTitle?: string;
    seoDescription?: string;
    slug?: string;
    keywords?: string[];
  };
  geo?: {
    geoSummary?: string;
    targetQuestions?: string[];
    keyFacts?: string[];
    entities?: string[];
  };
  bodyMarkdown?: string;
  createdAt: string;
  updatedAt: string;
}

export interface WorkBuddyTopicListQuery {
  page?: number;
  limit?: number;
  keyword?: string;
  status?: string;
}

export interface WorkBuddyTopicListResult {
  data: WorkBuddyTopicItem[];
  total: number;
  page: number;
  limit: number;
}

export const workbuddyTopicApi = {
  list: (params?: WorkBuddyTopicListQuery) =>
    request<WorkBuddyTopicListResult>('/bff/api/workbuddy/topics', {
      method: 'GET',
      params,
    }),
  get: (uuid: string) =>
    request<WorkBuddyTopicItem>(`/bff/api/workbuddy/topics/${uuid}`, {
      method: 'GET',
    }),
};

